import type { UIEventHandler } from 'react'
import type { PoiBasicInfoState } from '@/pages/poi/form'
import type { PoiType } from '@/shared/api/types/poi-types'
import { Loader2, Plus } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { usePoiCategoryByIdQuery } from '@/entities/poi/api/poi-queries'
import { POI_TYPES } from '@/shared/constants/poi-type'
import { resolvePoiCategoryName } from '@/shared/utils/poi-category-utils'
import { Button } from '@/shared/ui/button'
import { FormField } from '@/shared/ui/form-field'
import { Label } from '@/shared/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { Switch } from '@/shared/ui/switch'

export interface PoiCategoryOption {
  value: string
  label: string
  icon?: string
}

interface PoiBasicInfoFormProps {
  value: PoiBasicInfoState
  categories: PoiCategoryOption[]
  types: PoiType[]
  onChange: (value: PoiBasicInfoState) => void
  languageCode?: string
  isCategoriesLoading?: boolean
  hasCategoriesNextPage?: boolean
  isFetchingCategoriesNextPage?: boolean
  onLoadMoreCategories?: () => void
  onCreateCategory?: () => void
}

function getCategoryLabel(categories: PoiCategoryOption[], value: string): string | undefined {
  return categories.find((category) => category.value === value)?.label
}

function CategoryOptionContent({ category }: { category: PoiCategoryOption }) {
  return (
    <span className="flex min-w-0 items-center gap-2">
      {category.icon && (
        <img
          src={category.icon}
          alt=""
          className="h-5 w-5 shrink-0 rounded-sm object-cover"
          loading="lazy"
        />
      )}
      <span className="truncate">{category.label}</span>
    </span>
  )
}

export function PoiBasicInfoForm({
  value,
  categories,
  types,
  onChange,
  languageCode,
  isCategoriesLoading,
  hasCategoriesNextPage,
  isFetchingCategoriesNextPage,
  onLoadMoreCategories,
  onCreateCategory,
}: PoiBasicInfoFormProps) {
  const { t } = useTranslation()
  const categorySentinelRef = useRef<HTMLDivElement>(null)
  const isSelectedCategoryInList = useMemo(
    () => categories.some((category) => category.value === value.category),
    [categories, value.category],
  )
  const selectedCategoryQuery = usePoiCategoryByIdQuery(value.category ?? '', languageCode, {
    enabled: !isSelectedCategoryInList,
  })
  const selectedCategory = selectedCategoryQuery.data?.data

  const selectedCategoryOption = useMemo<PoiCategoryOption | undefined>(() => {
    if (!selectedCategory) {
      return undefined
    }
    const categoryValue = selectedCategory.id.trim()
    if (!categoryValue) {
      return undefined
    }
    return {
      value: categoryValue,
      label:
        resolvePoiCategoryName(selectedCategory, languageCode ? [languageCode] : undefined) ||
        categoryValue,
      ...(selectedCategory.icon ? { icon: selectedCategory.icon } : {}),
    }
  }, [languageCode, selectedCategory])

  const displayCategoryLabel = useMemo(() => {
    const labelFromList = getCategoryLabel(categories, value.category)
    if (labelFromList) {
      return labelFromList
    }
    return selectedCategoryOption?.label ?? value.category
  }, [categories, selectedCategoryOption, value.category])
  const handleCategoryScroll = useCallback<UIEventHandler<HTMLDivElement>>(
    (event) => {
      if (!hasCategoriesNextPage || isFetchingCategoriesNextPage || !onLoadMoreCategories) {
        return
      }
      const viewport = event.currentTarget
      const distanceToBottom = viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight
      if (distanceToBottom <= 24) {
        onLoadMoreCategories()
      }
    },
    [hasCategoriesNextPage, isFetchingCategoriesNextPage, onLoadMoreCategories],
  )

  useEffect(() => {
    const el = categorySentinelRef.current
    if (!el || !onLoadMoreCategories) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasCategoriesNextPage && !isFetchingCategoriesNextPage)
          onLoadMoreCategories()
      },
      { threshold: 0.1 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [hasCategoriesNextPage, isFetchingCategoriesNextPage, onLoadMoreCategories])

  return (
    <div className="space-y-6">
      <div className="grid gap-4 lg:grid-cols-2">
        <FormField label={t('poi.type')} required>
          <Select
            value={value.type}
            onValueChange={(type) => {
              if (type !== POI_TYPES.MAJOR && type !== POI_TYPES.MINOR) {
                return
              }
              onChange({
                ...value,
                type,
              })
            }}
          >
            <SelectTrigger className="h-11">
              <SelectValue placeholder={t('poi.selectType')}>
                {value.type
                  ? t(`poi.types.${value.type}`, { defaultValue: value.type })
                  : t('poi.selectType')}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {types.map((type) => (
                <SelectItem key={type} value={type}>
                  {t(`poi.types.${type}`, { defaultValue: type })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>

        <FormField label={t('poi.category')} required>
          <div className="space-y-2">
            <Select
              {...(value.category ? { value: value.category } : {})}
              onValueChange={(category) => onChange({ ...value, category })}
            >
              <SelectTrigger className="h-11">
                <SelectValue placeholder={t('poi.selectCategory')}>
                  {value.category ? displayCategoryLabel : t('poi.selectCategory')}
                </SelectValue>
              </SelectTrigger>
              <SelectContent onViewportScroll={handleCategoryScroll}>
                {isCategoriesLoading ? (
                  <div className="flex items-center justify-center gap-2 px-2 py-6 text-sm text-zinc-500">
                    <Loader2 size={14} className="animate-spin" />
                  </div>
                ) : categories.length === 0 ? (
                  <div className="space-y-3 px-3 py-5 text-center">
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-zinc-800">
                        {t('poi.categoryEmptyTitle', { defaultValue: 'No POI categories yet' })}
                      </p>
                      <p className="text-xs leading-5 text-zinc-500">
                        {t('poi.categoryEmptyDescription', {
                          defaultValue: 'Create a category first to classify this POI.',
                        })}
                      </p>
                    </div>
                    {onCreateCategory && (
                      <Button type="button" size="sm" className="h-8" onClick={onCreateCategory}>
                        <Plus className="mr-1.5 h-3.5 w-3.5" />
                        {t('poiCategories.create', { defaultValue: 'Create category' })}
                      </Button>
                    )}
                  </div>
                ) : (
                  <>
                    {categories.map((category) => (
                      <SelectItem key={category.value} value={category.value}>
                        <CategoryOptionContent category={category} />
                      </SelectItem>
                    ))}
                    {isFetchingCategoriesNextPage && (
                      <div className="flex items-center justify-center gap-2 py-2 text-xs text-zinc-400">
                        <Loader2 size={12} className="animate-spin" />
                      </div>
                    )}
                    <div ref={categorySentinelRef} className="h-1" />
                  </>
                )}
              </SelectContent>
            </Select>
            {onCreateCategory && categories.length > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-9 w-full justify-start text-sm font-medium text-blue-600 hover:text-blue-700"
                onClick={onCreateCategory}
              >
                <Plus className="mr-1.5 h-3.5 w-3.5 shrink-0" />
                <span className="truncate">
                  {t('poi.categoryCreateAction', { defaultValue: 'Create new category' })}
                </span>
              </Button>
            )}
          </div>
        </FormField>
      </div>

      <div className="rounded-2xl border border-zinc-200 bg-zinc-50/80 p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="space-y-1">
            <Label className="text-sm font-medium text-zinc-900">{t('poi.popupAd')}</Label>
            <p className="text-sm text-zinc-500">{t('poi.popupAdHint')}</p>
          </div>
          <Switch
            checked={value.popupAd}
            onCheckedChange={(popupAd) => onChange({ ...value, popupAd })}
          />
        </div>
      </div>
    </div>
  )
}
